import { getBuiltInManifest } from '@/data/manifests';
import { actionNameKey } from '@/hooks/useWorkflowConnectors';
import type { NodeConnectorInfo } from '@/types/connections';

export interface ToolDisplaySource {
	/** Connection reference name on the tool's inner action, if any. */
	connectionName?: string | undefined;
	/** Inner action name (the key inside `tool.actions`). */
	actionName?: string | undefined;
	/** Inner action `type` (e.g. 'Workflow', 'Compose'); drives the built-in fallback. */
	actionType?: string | undefined;
}

export interface ToolDisplay {
	iconUri?: string | undefined;
	brandColor?: string | undefined;
	displayName?: string | undefined;
	/** Where the values came from; lets callers tell "no connector hit yet" from a built-in fallback. */
	source: 'connector' | 'builtin' | 'none';
}

type ConnectorLookup = (key: string) => NodeConnectorInfo | undefined;

/**
 * Resolve a tool's icon / brand color / display name by walking three sources
 * in priority order: connection-name → namespaced action-name → built-in
 * manifest. Shared by the canvas node and the agent panel so they always
 * agree on what a tool looks like.
 */
export const resolveToolDisplay = (
	source: ToolDisplaySource,
	getConnectorInfo: ConnectorLookup,
): ToolDisplay => {
	const info =
		(source.connectionName ? getConnectorInfo(source.connectionName) : undefined) ??
		(source.actionName ? getConnectorInfo(actionNameKey(source.actionName)) : undefined);
	if (info) {
		return {
			iconUri: info.iconUri,
			brandColor: info.brandColor,
			displayName: info.displayName,
			source: 'connector',
		};
	}
	const manifest = source.actionType ? getBuiltInManifest(source.actionType) : null;
	if (!manifest) return { source: 'none' };
	return {
		iconUri: manifest.properties?.iconUri as string | undefined,
		brandColor: manifest.properties?.brandColor as string | undefined,
		displayName: manifest.properties?.summary as string | undefined,
		source: 'builtin',
	};
};
